import {
  BottomSheet,
  DialogFooter,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ThemeProvider } from "@mui/material";
import { useGetCredentialSharingConfiguration } from "@src/appV2/Facilities/api/useGetCredentialSharingConfiguration";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { type ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { Link } from "react-router-dom";

import { Button } from "../components/Button";
import { ChartingCredentialsSetUpContainer } from "./ChartingCredentialsSetUpContainer";
import { SHIFT_CREDENTIALS_CONFIRMATION_KEY } from "./constants";

type ShiftCredentialsConfirmation = Record<string, boolean>;

interface ClockInChartingReminderProps {
  chartingReminderModalState: UseModalState;
  chartingCredentialsModalState?: UseModalState;
  shiftId: string;
  workplaceId: string;
  chartingCredential?: ChartingCredential;
  chartingCredentialSharingIsEnabled?: boolean;
  onClose?: () => void;
}

export function ClockInChartingReminder({
  chartingReminderModalState,
  chartingCredentialsModalState,
  shiftId,
  workplaceId,
  chartingCredential,
  chartingCredentialSharingIsEnabled = false,
  onClose,
}: ClockInChartingReminderProps) {
  // Local storage for tracking confirmation
  const [confirmedShifts, setConfirmedShifts] = useLocalStorage<ShiftCredentialsConfirmation>(
    SHIFT_CREDENTIALS_CONFIRMATION_KEY,
    {}
  );

  const { credentialSharingConfiguration, isLoading } = useGetCredentialSharingConfiguration(
    workplaceId,
    {
      enabled: isDefined(workplaceId),
    }
  );
  const { chartingReminders } = credentialSharingConfiguration ?? {};

  const hasChartingCredentials =
    chartingCredentialSharingIsEnabled &&
    isDefined(chartingCredential) &&
    (isDefined(chartingCredential.username) || isDefined(chartingCredential.password));

  if (isLoading) {
    return null;
  }

  // If the new feature flag is enabled and modal is open, render the new credentials container
  if (chartingReminderModalState.modalIsOpen && chartingReminders?.clockIn?.enabled) {
    return (
      <ChartingCredentialsSetUpContainer
        shiftId={shiftId}
        workplaceId={workplaceId}
        onComplete={() => {
          chartingReminderModalState.closeModal();
          onClose?.();
        }}
      />
    );
  }

  // Check if worker has already confirmed credentials for this shift
  const hasConfirmedCredentials = confirmedShifts[shiftId];

  // If credentials are already confirmed, render nothing
  if (hasConfirmedCredentials) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet modalState={chartingReminderModalState} canDismiss={false}>
        <Box sx={{ paddingX: 4, paddingTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
          <Text bold variant="h4" sx={{ textAlign: "center" }}>
            You&rsquo;ve clocked in! Do you have your charting login?
          </Text>

          <Box sx={{ marginTop: 6, display: "flex", justifyContent: "center" }}>
            <Illustration type="quiz-required" />
          </Box>

          <Text
            variant="body1"
            sx={{
              marginTop: 6,
              paddingX: 2,
              textAlign: "center",
              lineHeight: 1.3,
              textWrap: "balance",
            }}
          >
            Charting is a part of the job. Make sure you have your login before you start!
          </Text>
        </Box>
        <DialogFooter orientation="vertical" LinkComponent={Link}>
          {hasChartingCredentials && (
            <>
              <Button
                fullWidth
                size="large"
                role="button"
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.CHARTING_REMINDER_VIEW_MY_LOGIN, {
                    shiftId,
                    workplaceId,
                  });
                  chartingReminderModalState.closeModal();
                  chartingCredentialsModalState?.openModal();
                }}
              >
                View my login
              </Button>

              <Button
                fullWidth
                size="large"
                role="button"
                variant="outlined"
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.CLOCK_IN_CHARTING_REMINDER_ACKNOWLEDGED, {
                    shiftId,
                    workplaceId,
                    action: "already-have-login",
                  });
                  // Record that the login has been confirmed
                  setConfirmedShifts((previous) => ({
                    ...previous,
                    [shiftId]: true,
                  }));
                  chartingReminderModalState.closeModal();
                  onClose?.();
                }}
              >
                I already know my login
              </Button>
            </>
          )}
          {!hasChartingCredentials && (
            <>
              <Button
                fullWidth
                size="large"
                role="button"
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.CLOCK_IN_CHARTING_REMINDER_ACKNOWLEDGED, {
                    shiftId,
                    workplaceId,
                    action: "already-have-login",
                  });
                  // Record that the login has been confirmed
                  setConfirmedShifts((previous) => ({
                    ...previous,
                    [shiftId]: true,
                  }));
                  chartingReminderModalState.closeModal();
                  onClose?.();
                }}
              >
                I already have my login
              </Button>

              <Button
                fullWidth
                size="large"
                role="button"
                variant="outlined"
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.CHARTING_REMINDER_CLOCK_IN_REQUEST_CREDENTIALS, {
                    shiftId,
                    workplaceId,
                    action: "request-credentials",
                  });
                  chartingReminderModalState.closeModal();
                  onClose?.();
                }}
              >
                I do not, but I&apos;ll ask for them now
              </Button>
            </>
          )}
        </DialogFooter>
      </BottomSheet>
    </ThemeProvider>
  );
}
